import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CircleHelp, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import type { Tier } from '../lib/detectContract';
import { useStore } from '../state/store';

/** Compact navigation bar for pushed screens: back, centred title, trailing action. */
export function TopBar({ title, back, right, sub }: { title?: ReactNode; back?: string | (() => void); right?: ReactNode; sub?: ReactNode }) {
  const navigate = useNavigate();
  const onBack = () => {
    if (typeof back === 'function') back();
    else if (window.history.state?.idx > 0) navigate(-1);
    else navigate(back ?? '/today', { replace: true });
  };
  return (
    <header className="flex items-center gap-1 -mx-2 mb-2 min-h-[44px]">
      {back !== undefined ? (
        <button className="icon-btn text-primary" onClick={onBack} aria-label="Back">
          <ChevronLeft size={28} strokeWidth={2.2} aria-hidden />
        </button>
      ) : <span className="w-11" aria-hidden />}
      <div className="flex-1 min-w-0 text-center">
        {title && <h1 className="text-headline truncate">{title}</h1>}
        {sub && <p className="text-footnote text-fg-muted truncate">{sub}</p>}
      </div>
      {right ?? <span className="w-11" aria-hidden />}
    </header>
  );
}

/** Large title header used at the top of tab screens. */
export function LargeTitle({ eyebrow, title, right }: { eyebrow?: ReactNode; title: ReactNode; right?: ReactNode }) {
  return (
    <header className="flex items-end gap-3 mb-5 pt-2">
      <div className="flex-1 min-w-0">
        {eyebrow && <p className="eyebrow mb-0.5">{eyebrow}</p>}
        <h1 className="text-large-title">{title}</h1>
      </div>
      {right}
    </header>
  );
}

export function SectionHeader({ id, title, detail, action }: { id?: string; title: ReactNode; detail?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-end gap-3 mb-3">
      <div className="flex-1 min-w-0">
        <h2 id={id} className="section-title">{title}</h2>
        {detail && <p className="text-subhead text-fg-muted mt-0.5">{detail}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon, title, body, children }: { icon: ReactNode; title: string; body?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-14 fade-in">
      <div className="w-14 h-14 rounded-full bg-fill text-fg-muted flex items-center justify-center mb-4" aria-hidden>
        {icon}
      </div>
      <h2 className="text-title-3 mb-2">{title}</h2>
      {body && <p className="text-subhead text-fg-muted mb-6 max-w-[34ch]">{body}</p>}
      <div className="flex flex-col gap-2 w-full max-w-xs">{children}</div>
    </div>
  );
}

const TIER_META: Record<Tier, { label: string; cls: string; Icon: typeof CircleCheck }> = {
  sure: { label: 'Sure', cls: 'badge-primary', Icon: CircleCheck },
  check: { label: 'Check', cls: 'badge-warning', Icon: TriangleAlert },
  maybe: { label: 'Not sure', cls: 'badge-neutral', Icon: CircleHelp },
};

/** Confidence is always shown as icon + word, never a percentage or colour alone. */
export function ConfidenceBadge({ tier }: { tier: Tier }) {
  const { label, cls, Icon } = TIER_META[tier];
  return (
    <span className={`badge ${cls}`}>
      <Icon size={12} aria-hidden /> {label}
    </span>
  );
}

export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    node?.querySelector<HTMLElement>('button, [href], input, select, textarea')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && node) {
        const f = [...node.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input, select, textarea')];
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; prev?.focus?.(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 scrim-in" onClick={onClose} aria-hidden />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId}
        className="relative w-full max-w-[520px] bg-surface rounded-t-[20px] sm:rounded-[20px] sheet-in max-h-[90dvh] flex flex-col shadow-2xl">
        <div className="pt-2 flex justify-center sm:hidden" aria-hidden><span className="w-9 h-[5px] rounded-full bg-fill-strong" /></div>
        <div className="flex items-center justify-between px-5 pt-2 pb-2">
          <h2 id={titleId} className="text-title-3">{title}</h2>
          <button className="icon-btn icon-btn-filled -mr-1 w-8 h-8 min-w-[44px] min-h-[44px] bg-transparent" onClick={onClose} aria-label="Close">
            <span className="w-[30px] h-[30px] rounded-full bg-fill flex items-center justify-center"><X size={16} strokeWidth={2.6} aria-hidden /></span>
          </button>
        </div>
        <div className="px-5 pb-5 overflow-y-auto" style={{ paddingBottom: footer ? 16 : 'max(20px, var(--safe-bottom))' }}>{children}</div>
        {footer && <div className="px-5 pt-2 border-t border-line" style={{ paddingBottom: 'max(16px, var(--safe-bottom))' }}>{footer}</div>}
      </div>
    </div>
  );
}

export function ToastHost() {
  const toast = useStore((s) => s.toast);
  const dismiss = useStore((s) => s.dismissToast);
  return (
    <div className="fixed left-0 right-0 z-[60] flex justify-center px-4 pointer-events-none"
      style={{ bottom: 'calc(var(--safe-bottom) + 96px)' }} role="status" aria-live="polite">
      {toast && (
        <div key={toast.id} className="pointer-events-auto fade-in max-w-[440px] w-full flex items-center gap-3 rounded-2xl pl-4 pr-2 py-2 shadow-xl bg-inverse text-inverse-on">
          <span className="flex-1 text-subhead font-medium py-2">{toast.message}</span>
          {toast.action && (
            <button className="font-semibold text-subhead min-h-[44px] px-3 rounded-xl hover:bg-white/10"
              onClick={() => { toast.action!.run(); dismiss(); }}>
              {toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A shimmering placeholder block in the shape of what's coming. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span className={`skeleton block rounded-[10px] ${className}`} aria-hidden />;
}

/** Stand-in for a screen while its code loads: title, a search bar and cards, not a spinner. */
export function PageSkeleton() {
  return (
    <main className="screen screen-soft" role="status" aria-label="Loading">
      <div className="max-w-[720px] mx-auto">
        <Skeleton className="h-8 w-44 mt-1 mb-5" />
        <Skeleton className="h-12 w-full rounded-[14px] mb-6" />
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="tile overflow-hidden block">
              <Skeleton className="aspect-[4/3] rounded-none" />
              <span className="block p-3.5"><Skeleton className="h-4 w-4/5 mb-2" /><Skeleton className="h-3 w-1/2" /></span>
            </span>
          ))}
        </div>
      </div>
    </main>
  );
}

/** Stand-in for a recipe: the photo, title, facts and the first ingredient rows. */
export function RecipeSkeleton() {
  return (
    <main className="min-h-[100dvh]" role="status" aria-label="Loading recipe">
      <Skeleton className="w-full aspect-[4/5] max-h-[70dvh] md:max-h-[520px] rounded-none" />
      <div className="screen pt-6">
        <Skeleton className="h-3 w-20 mb-3" />
        <Skeleton className="h-8 w-3/4 mb-3" />
        <Skeleton className="h-4 w-1/2 mb-8" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-5 w-full mb-4" />)}
      </div>
    </main>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2">
      <span className="w-5 h-5 rounded-full border-2 border-current border-r-transparent animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Notice({ tone = 'warning', children, icon }: { tone?: 'warning' | 'info' | 'danger'; children: ReactNode; icon?: ReactNode }) {
  const cls = tone === 'warning' ? 'bg-warning-bg text-warning-fg' : tone === 'danger' ? 'bg-danger-bg text-danger' : 'bg-fill text-fg';
  return (
    <div className={`flex gap-2.5 items-start rounded-xl px-3.5 py-3 text-subhead ${cls}`}>
      <span className="mt-0.5 shrink-0" aria-hidden>{icon ?? (tone === 'info' ? <Info size={16} /> : <TriangleAlert size={16} />)}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/** iOS-style grouped list. Children should be <Row>s or custom rows. */
export function Group({ title, footer, children, className = '' }: { title?: ReactNode; footer?: ReactNode; children: ReactNode; className?: string }) {
  const id = useId();
  return (
    <section className={`mb-7 ${className}`} aria-labelledby={title ? id : undefined}>
      {title && <h2 id={id} className="group-title">{title}</h2>}
      <div className="group">{children}</div>
      {footer && <p className="group-footer">{footer}</p>}
    </section>
  );
}

export function Row({ title, detail, value, onClick, to, icon, danger, trailing, chevron = true }: {
  title: ReactNode; detail?: ReactNode; value?: ReactNode; onClick?: () => void; to?: string; icon?: ReactNode;
  danger?: boolean; trailing?: ReactNode; chevron?: boolean;
}) {
  const inner = (
    <>
      {icon && <span className="shrink-0 text-fg-muted" aria-hidden>{icon}</span>}
      <span className="flex-1 min-w-0 py-3">
        <span className={`block text-body ${danger ? 'text-danger' : ''}`}>{title}</span>
        {detail && <span className="block text-subhead text-fg-muted">{detail}</span>}
      </span>
      {value && <span className="text-body text-fg-muted truncate max-w-[45%] text-right">{value}</span>}
      {trailing}
      {(to || onClick) && chevron && <ChevronRight size={18} className="text-fg-muted/60 shrink-0 -mr-1" aria-hidden />}
    </>
  );
  const cls = 'w-full flex items-center gap-3 px-4 min-h-[48px] text-left';
  if (to) return <Link to={to} className={`${cls} active:bg-fill`}>{inner}</Link>;
  if (onClick) return <button className={`${cls} active:bg-fill`} onClick={onClick}>{inner}</button>;
  return <div className={cls}>{inner}</div>;
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" onClick={() => onChange(!checked)} />;
}

export function SwitchRow({ title, detail, checked, onChange }: { title: string; detail?: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return <Row title={title} detail={detail} chevron={false} trailing={<Switch checked={checked} onChange={onChange} label={title} />} />;
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="segmented">
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="radio" aria-checked={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Thin progress indicator for multi-step flows. */
export function Steps({ step, total, label }: { step: number; total: number; label?: string }) {
  return (
    <div className="flex items-center gap-3" aria-label={label ?? `Step ${step} of ${total}`} role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={step}>
      <div className="flex-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={`h-1 rounded-full transition-colors duration-300 ${i < step ? 'bg-fg' : 'bg-fill-strong'}`} />
        ))}
      </div>
      <span className="text-footnote text-fg-muted tabular shrink-0">{step} of {total}</span>
    </div>
  );
}

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor: string }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-subhead font-semibold mb-1.5">{label}</label>
      {children}
      {error ? <p id={`${htmlFor}-error`} className="text-footnote text-danger mt-1.5" role="alert">{error}</p>
        : hint ? <p id={`${htmlFor}-hint`} className="text-footnote text-fg-muted mt-1.5">{hint}</p> : null}
    </div>
  );
}
